import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, styled } from "@mui/material";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ModalDialogTransition } from "../animations/ModalDialogTransition";
import { ProgressButton } from "../progressButton/ProgressButton";
import { handleAsyncCallback } from "../util/asyncHelpers";

type ConfirmationModal = {
  isOpen: boolean;
  noCancelButton?: boolean;
  confirmCb: () => Promise<void> | void;
  closeCb: () => void;
  modalTitle: string;
  modalText?: ReactNode;
  listToDisplay?: string[];
  loader?: boolean;
  disableConfirmButton?: boolean;
  confirmText?: string;
  /**
   * Required id for the dialog title, used as `aria-labelledby` for accessibility
   * and to prevent id conflicts in tests.
   */
  labelledById: string;
  isConfirmLoading?: boolean;
  isCanceledDisabled?: boolean;
} & Partial<DialogProps>;

const DialogText = styled(DialogContentText)(({ theme }) => ({
  textAlign: "center",
  fontSize: "20px",
  fontWeight: 400,
  paddingTop: theme.spacing(2)
}));

/**
 * A modal that prompts for confirmation, and optionally an option to cancel.
 */
export function ConfirmationModal(
  props: ConfirmationModal
): React.ReactElement {
  const {
    isOpen,
    noCancelButton,
    closeCb,
    confirmCb,
    modalTitle,
    modalText,
    listToDisplay,
    loader,
    disableConfirmButton,
    confirmText,
    labelledById,
    isConfirmLoading,
    isCanceledDisabled,
    ...rest
  } = props;

  const { t } = useTranslation();

  const handleClose = (): void => {
    closeCb();
  };

  const handleConfirm = async (): Promise<void> => {
    await handleAsyncCallback(confirmCb);
  };

  return (
    <Dialog
      open={isOpen}
      TransitionComponent={ModalDialogTransition}
      keepMounted
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={labelledById}
      aria-describedby={
        modalText ? "confirmation-modal-description" : undefined
      }
      {...rest}
    >
      <DialogTitle
        id={labelledById}
        sx={{
          textAlign: "center"
        }}
      >
        {modalTitle}
      </DialogTitle>

      {loader && (
        <DialogContent
          role="region"
          aria-label="Loading confirmation modal"
          aria-busy="true"
          sx={{ textAlign: "center", mt: 4 }}
        >
          <CircularProgress color="inherit" />
        </DialogContent>
      )}
      <DialogContent>
        {modalText && (
          <DialogText id="confirmation-modal-description">
            {modalText}
          </DialogText>
        )}
      </DialogContent>
      {listToDisplay && (
        <DialogContent>
          {listToDisplay.map((el, index) => (
            <DialogContentText key={`description-${index}`}>
              {el}
            </DialogContentText>
          ))}
        </DialogContent>
      )}
      <DialogActions sx={{ justifyContent: "center", mb: 2 }}>
        {!noCancelButton && (
          <ProgressButton
            onClick={handleClose}
            color="secondary"
            startIcon={<CloseIcon />}
            buttonSize="medium"
            emphasis="high"
            responsive
            fullWidth
            disabled={isCanceledDisabled}
          >
            {t("cancel")}
          </ProgressButton>
        )}

        <ProgressButton
          onClick={handleConfirm}
          color="primary"
          startIcon={<CheckIcon />}
          buttonSize="medium"
          emphasis="high"
          loading={isConfirmLoading}
          responsive
          fullWidth
          disabled={disableConfirmButton ? true : false}
        >
          {confirmText || t("confirm")}
        </ProgressButton>
      </DialogActions>
    </Dialog>
  );
}
